/**
 * Authorization code flow utilities for Keycloak OAuth (no PKCE).
 * Requires the Keycloak client to be configured as "Public" access type.
 */
import { authConfig } from './authConfig';

const ACCESS_TOKEN_COOKIE = 'csp_access_token';
const REFRESH_TOKEN_COOKIE = 'csp_refresh_token';
/** Short-lived cookie for OAuth `state` (authorization round-trip only). */
const OAUTH_STATE_COOKIE = 'csp_oauth_state';
const OAUTH_STATE_MAX_AGE_SEC = 600;

export interface AuthTokens {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_in: number;
}

/** User info displayed in the navbar; decoded from the access token JWT */
export interface AuthUser {
  name?: string;
  email?: string;
}

export const DEFAULT_REALM_ROLE = 'default-roles-employee';

interface RealmAccessClaims {
  roles?: unknown;
}

interface AccessTokenClaims {
  realm_access?: RealmAccessClaims;
}

function parseJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '='
    );
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Milliseconds since epoch from JWT `exp`, if present */
function getJwtExpiryMs(token: string): number | null {
  const payload = parseJwtPayload(token);
  const exp = payload?.exp;
  return typeof exp === 'number' ? exp * 1000 : null;
}

function cookieSecuritySuffix(): string {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  return `; Path=/; SameSite=Lax${secure}`;
}

function readCookie(name: string): string | null {
  const prefixed = `${name}=`;
  const parts = document.cookie.split('; ');
  for (const part of parts) {
    if (part.startsWith(prefixed)) {
      return decodeURIComponent(part.slice(prefixed.length));
    }
  }
  return null;
}

function writeCookie(
  name: string,
  value: string,
  maxAgeSeconds: number
): void {
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSeconds}${cookieSecuritySuffix()}`;
}

function deleteCookie(name: string): void {
  document.cookie = `${name}=; Max-Age=0${cookieSecuritySuffix()}`;
}

function maxAgeSecondsForToken(token: string, fallbackSeconds: number): number {
  const expMs = getJwtExpiryMs(token);
  if (expMs === null) return fallbackSeconds;
  return Math.max(1, Math.ceil((expMs - Date.now()) / 1000));
}

function persistTokensToCookies(tokens: AuthTokens): void {
  const accessMax = maxAgeSecondsForToken(
    tokens.access_token,
    Math.max(60, tokens.expires_in || 3600)
  );
  writeCookie(ACCESS_TOKEN_COOKIE, tokens.access_token, accessMax);

  if (tokens.refresh_token) {
    const refreshMax = maxAgeSecondsForToken(
      tokens.refresh_token,
      60 * 60 * 24 * 30
    );
    writeCookie(REFRESH_TOKEN_COOKIE, tokens.refresh_token, refreshMax);
  } else {
    deleteCookie(REFRESH_TOKEN_COOKIE);
  }

  deleteCookie('csp_id_token');
  deleteCookie('csp_auth_user');
}

function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function initiateLogin(): void {
  const state = generateRandomString(32);
  writeCookie(OAUTH_STATE_COOKIE, state, OAUTH_STATE_MAX_AGE_SEC);

  const params = new URLSearchParams({
    client_id: authConfig.clientId,
    redirect_uri: authConfig.getRedirectUri(),
    response_type: 'code',
    scope: 'openid',
    state,
  });

  if (authConfig.idpHint) {
    params.set('kc_idp_hint', authConfig.idpHint);
  }

  window.location.href = `${authConfig.authEndpoint}?${params.toString()}`;
}

export function getStoredState(): string | null {
  return readCookie(OAUTH_STATE_COOKIE);
}

export function clearLoginState(): void {
  deleteCookie(OAUTH_STATE_COOKIE);
}

export const AUTH_UPDATED_EVENT = 'auth_tokens_updated';

export function saveTokens(tokens: AuthTokens): void {
  persistTokensToCookies(tokens);
  window.dispatchEvent(new Event(AUTH_UPDATED_EVENT));
}

function expiresInFromAccessToken(accessToken: string): number {
  const expMs = getJwtExpiryMs(accessToken);
  if (expMs === null) return 0;
  return Math.max(0, Math.floor((expMs - Date.now()) / 1000));
}

export function getTokens(): AuthTokens | null {
  const access_token = readCookie(ACCESS_TOKEN_COOKIE);
  if (!access_token) return null;

  const expMs = getJwtExpiryMs(access_token);
  if (expMs !== null && Date.now() >= expMs) {
    return null;
  }

  const refresh_token = readCookie(REFRESH_TOKEN_COOKIE) ?? undefined;

  return {
    access_token,
    refresh_token,
    expires_in: expiresInFromAccessToken(access_token),
  };
}

export function getTokenExpiryMs(): number | null {
  const access = readCookie(ACCESS_TOKEN_COOKIE);
  if (!access) return null;
  return getJwtExpiryMs(access);
}

export function getMsUntilExpiry(): number | null {
  const expiresAt = getTokenExpiryMs();
  if (!expiresAt) return null;
  return expiresAt - Date.now();
}

export function isSessionExpired(): boolean {
  const ms = getMsUntilExpiry();
  return ms !== null && ms <= 0;
}

export function clearTokens(): void {
  deleteCookie(ACCESS_TOKEN_COOKIE);
  deleteCookie(REFRESH_TOKEN_COOKIE);
  deleteCookie('csp_id_token');
  deleteCookie('csp_auth_user');
  window.dispatchEvent(new Event(AUTH_UPDATED_EVENT));
}

export function getUser(): AuthUser | null {
  const access = readCookie(ACCESS_TOKEN_COOKIE);
  if (!access) return null;
  const expMs = getJwtExpiryMs(access);
  if (expMs !== null && Date.now() >= expMs) return null;
  return parseUserFromAccessToken(access);
}

/** Backend can return tokens and optionally user; id_token is not persisted */
export interface SignInResponse extends AuthTokens {
  user?: AuthUser;
}

function authUserFromJwtPayload(
  payload: Record<string, unknown> | null
): AuthUser | null {
  if (!payload) return null;
  const name =
    typeof payload.name === 'string'
      ? payload.name
      : typeof payload.preferred_username === 'string'
        ? payload.preferred_username
        : undefined;
  const email =
    typeof payload.email === 'string' ? payload.email : undefined;
  if (!name && !email) return null;
  return { name: name || undefined, email: email || undefined };
}

/**
 * Decodes the payload of a JWT without verifying (client-only; used for display).
 * Keycloak id_token often includes name, email, preferred_username.
 */
export function parseIdTokenClaims(idToken: string): AuthUser | null {
  return authUserFromJwtPayload(parseJwtPayload(idToken));
}

/** Profile fields from the access token (Keycloak may map name/email into it). */
export function parseUserFromAccessToken(accessToken: string): AuthUser | null {
  return authUserFromJwtPayload(parseJwtPayload(accessToken));
}

export function parseRealmRolesFromAccessToken(accessToken: string): string[] {
  const payload = parseJwtPayload(accessToken) as AccessTokenClaims | null;
  const rawRoles = payload?.realm_access?.roles;
  if (!Array.isArray(rawRoles)) return [];
  return rawRoles.filter((role): role is string => typeof role === 'string');
}

export function getDisplayRoles(realmRoles: string[]): string[] {
  return realmRoles.filter((role) => role !== DEFAULT_REALM_ROLE);
}

const ROLE_LABELS: Record<string, string> = {
  COMPLIANCE: 'Compliance',
  OPERATIONS: 'Operations',
  SUPPORT_AGENT: 'Support Agent',
  FINANCE: 'Finance',
};

const ROLE_DISPLAY_PRIORITY = ['SUPPORT_AGENT', 'COMPLIANCE', 'OPERATIONS', 'FINANCE'] as const;

export function toRoleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

export function getPrimaryDisplayRole(realmRoles: string[]): string | null {
  const appRoles = getDisplayRoles(realmRoles);
  const prioritizedRole = ROLE_DISPLAY_PRIORITY.find((role) =>
    appRoles.includes(role)
  );
  const selectedRole = prioritizedRole ?? appRoles[0];
  return selectedRole ? toRoleLabel(selectedRole) : null;
}

/** Two-letter initials for avatar display (first + last word, or first two chars). */
export function getNameInitials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const a = parts[0][0];
    const b = parts[parts.length - 1][0];
    if (a && b) return `${a}${b}`.toUpperCase();
  }
  return displayName.slice(0, 2).toUpperCase() || '?';
}

// When REACT_APP_API_BASE_URL is set (e.g. https://backendstg.empirefx.com/efx-csp), API base
// is that URL + /api/v1. When unset, dev proxy is used: /efx-csp/api/v1.
const apiBaseUrl = process.env.REACT_APP_API_BASE_URL
  ? `${process.env.REACT_APP_API_BASE_URL}/api/v1`
  : '/efx-csp/api/v1';

const SIGN_IN_ENDPOINT = `${apiBaseUrl}/authentication/sign-in`;
const LOGOUT_ENDPOINT = `${apiBaseUrl}/authentication/logout`;

/**
 * Calls the backend logout endpoint to invalidate the server-side session.
 * Errors are silently ignored so client-side cleanup always proceeds.
 */
export async function callLogoutApi(): Promise<void> {
  const tokens = getTokens();
  if (!tokens?.access_token) return;
  try {
    await fetch(LOGOUT_ENDPOINT, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${tokens.access_token}`,
      },
    });
  } catch {
    // ignore — we still want to clear local session
  }
}

/**
 * Sends the Keycloak/Google authorization code to the backend.
 * Backend exchanges the code with Keycloak server-side and returns tokens.
 */
export async function signInWithCode(code: string): Promise<SignInResponse> {
  let response: Response;
  try {
    response = await fetch(SIGN_IN_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code,
        redirectUri: authConfig.getRedirectUri(),
      }),
    });
  } catch {
    throw new Error(
      'Unable to reach the authentication server. This may be a network or CORS issue. Please contact support if the problem persists.'
    );
  }

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Sign-in failed: ${response.status} ${text}`);
  }

  return response.json();
}
